// src/features/TechnicianAnalysis/TechnicianReportViewPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Chip, Stack, Button, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { ArrowBack, Download, Print } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es, uk, ca, enGB } from 'date-fns/locale';
import { useI18n } from '../../i18n/i18n';
import { getData } from '../../services/storageService';

const TechnicianReportViewPage = () => {
  const { t, lang } = useI18n();
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const locales = { es, uk, ca, en: enGB };

  useEffect(() => {
    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    try {
      const reports = await getData('technician_reports') || [];
      const foundReport = reports.find(r => r.id === reportId);
      
      if (foundReport) {
        setReport(foundReport);
      } else {
        // Якщо звіт не знайдено, показуємо демо-дані
        setReport(generateDemoReport(reportId));
      }
    } catch (error) {
      console.error('Error loading report:', error);
      setReport(generateDemoReport(reportId));
    } finally {
      setLoading(false);
    }
  };

  const generateDemoReport = (id) => {
    const demoData = {
      id: id,
      date: '2024-01-15',
      createdBy: 'Juan García',
      status: 'completed',
      equipmentCount: 8,
      sections: {
        calderas: {
          hora: '08:30',
          nivel_agua: '85%',
          presion: '2.5',
          temp_acs: '65',
          dureza: '120',
          cloro: '0.8',
          ph: '7.2',
          conductividad: '450',
          nivel_sal: '75%',
          contador: '12450'
        },
        introductor: {
          via1_val1: '45',
          via1_val2: '38',
          via2_val1: '52',
          via2_val2: '41',
          via3_val1: '48',
          via3_val2: '39',
          via4_val1: '51',
          via4_val2: '42',
          via5_val1: '47',
          via5_val2: '40'
        },
        hibrida: {
          piezasGrandes: '23',
          via1_piezas: '12',
          via2_piezas: '11',
          trabajador_via1: 'Maria Lopez',
          trabajador_via2: 'Carlos Ruiz',
          trabajador_via3: 'Ana Martinez'
        },
        kannegiesser: [
          {
            id: 1,
            p1: '15',
            p2: '12',
            p3: '18',
            p4: '14',
            trabajador1: 'Pedro Sanchez',
            trabajador2: 'Laura Gomez',
            trabajador3: 'David Torres'
          }
        ]
      },
      notes: 'Todo el equipo funcionando correctamente. Se realizó mantenimiento preventivo en lavadora L-12.'
    };
    
    return demoData;
  };

  const handleDownloadPDF = () => {
    // Генерація PDF звіту
    console.log('Generating PDF for report:', reportId);
    alert(`${t('actions_download_pdf')}: ${reportId}\n${t('export_development')}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'PPPP', { locale: locales[lang] });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>{t('common_loading')}</Typography>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          {t('view_record.errors.not_found')}
        </Typography>
        <Button onClick={() => navigate('/archive/technician')}>
          {t('view_record.actions.back_to_archive')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
            <Box>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/archive/technician')}
                sx={{ mb: 2 }}
              >
                {t('view_record.actions.back_to_archive')}
              </Button>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {t('tech_report_title')} - {report.id}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                <Chip 
                  label={`${t('labels_date')}: ${formatDate(report.date)}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip 
                  label={`${t('common_technician')}: ${report.createdBy}`}
                  color="secondary"
                  variant="outlined"
                />
                <Chip 
                  label={`${t('common_status')}: ${t(`status_${report.status}`)}`}
                  color={getStatusColor(report.status)}
                  variant="filled"
                />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadPDF}
              >
                {t('actions_download_pdf')}
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
              >
                {t('common_print')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Report Content */}
      <Grid container spacing={3}>
        {/* Calderas Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                {t('tech_form_tab_calderas')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {report.sections?.calderas && Object.entries(report.sections.calderas).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell><strong>{t(`field_${key}`)}</strong></TableCell>
                        <TableCell>{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Introductor Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                {t('tech_form_tab_introductor')}
              </Typography>
              <Grid container spacing={1}>
                {report.sections?.introductor && Object.entries(report.sections.introductor).map(([key, value]) => (
                  <Grid item xs={6} key={key}>
                    <Paper sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" display="block">
                        {key.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Hibrida Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                {t('tech_form_tab_hibrida')}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2">{t('field_piezas_grandes')}</Typography>
                  <Typography variant="h5">{report.sections?.hibrida?.piezasGrandes}</Typography>
                </Box>
                <Divider />
                <Typography variant="subtitle2">{t('section_trabajadores')}</Typography>
                <Stack spacing={1}>
                  <Typography><strong>VÍA 1:</strong> {report.sections?.hibrida?.trabajador_via1}</Typography>
                  <Typography><strong>VÍA 2:</strong> {report.sections?.hibrida?.trabajador_via2}</Typography>
                  <Typography><strong>VÍA 3:</strong> {report.sections?.hibrida?.trabajador_via3}</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Kannegiesser Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                {t('tech_form_tab_kannegiesser')}
              </Typography>
              {report.sections?.kannegiesser?.map((machine, index) => (
                <Box key={machine.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('kannegiesser_machine_no')} {index + 1}
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={3}>
                      <Typography variant="caption">{t('field_pieza1')}</Typography>
                      <Typography variant="body2" fontWeight="bold">{machine.p1}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption">{t('field_pieza2')}</Typography>
                      <Typography variant="body2" fontWeight="bold">{machine.p2}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption">{t('field_pieza3')}</Typography>
                      <Typography variant="body2" fontWeight="bold">{machine.p3}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption">{t('field_pieza4')}</Typography>
                      <Typography variant="body2" fontWeight="bold">{machine.p4}</Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="caption">{t('section_trabajadores')}</Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">{t('kannegiesser_worker_no')} 1: {machine.trabajador1}</Typography>
                    <Typography variant="body2">{t('kannegiesser_worker_no')} 2: {machine.trabajador2}</Typography>
                    <Typography variant="body2">{t('kannegiesser_worker_no')} 3: {machine.trabajador3}</Typography>
                  </Stack>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
{/* Photos Section */}
{report.photos && report.photos.length > 0 && (
  <Grid item xs={12}>
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary">
          {t('technician_photos')} ({report.photos.length})
        </Typography>
        <Grid container spacing={2}>
          {report.photos.map((photo, index) => (
            <Grid item xs={12} sm={6} md={4} key={photo.id || index}>
              <Card variant="outlined">
                <Box
                  sx={{
                    height: 200,
                    overflow: 'hidden',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(photo.url, '_blank')}
                >
                  <img
                    src={photo.url}
                    alt={photo.description || `Photo ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
                <CardContent sx={{ p: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {photo.timestamp ? new Date(photo.timestamp).toLocaleString() : ''}
                  </Typography>
                  {photo.description && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {photo.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  </Grid>
)}
        {/* Notes Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                {t('common_notes')}
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography>
                  {report.notes || t('common_none')}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TechnicianReportViewPage;